import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiDetail } from "../../lib/api";
import { StatusPill, STATUS_PILL_ROW_COMPACT_CLASS } from "../../components/StatusPill";
import InvoiceCreateDialog from "../../components/admin/InvoiceCreateDialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Switch } from "../../components/ui/switch";
import { downloadPacketByFileId, sendPacket } from "../../lib/packetApi";
import { cn } from "../../lib/utils";
import { isLocalApp } from "../../lib/env";
import { getMockBarterClient, isMockBarterClientId, MOCK_BARTER_AGREEMENT_ID, MOCK_BARTER_AGREEMENT_PATH, MOCK_BARTER_AGREEMENT_STUDIO_PATH } from "../../lib/mockBarterClients";
import {
  BARTER_EDITABLE_FIELDS,
  BarterAgreementSignBox,
  MOCK_BARTER_AGREEMENT_FIELDS,
  barterAgreementFileName,
  barterAgreementStatusLabel,
  formatBarterStartDate,
  normalizeBarterFields,
  seedBarterFieldsFromClient,
  toBarterStartDateIso,
} from "../../components/BarterAgreementDocument";
import { InlineDatePickerField } from "../../components/DatePickerField";
import { openAppPathInNewTab } from "../../lib/portalFiles";
import { agreementPreviewPath, clientSlug, portalBasePath } from "../../lib/portalPaths";
import {
  clearUploadedProposalDecision,
  clearUploadedProposal,
  ensureMockUploadedProposal,
  markProposalShared,
  normalizeProposalOption,
  proposalOptionsDraft,
  PROPOSAL_TIMELINES,
  resetUploadedProposal,
  resolveUploadedProposal,
  saveProposalFromFields,
  setUploadedProposalInvoiceId,
  acceptUploadedProposal,
  optionsPayloadForApi,
  uploadedProposalSharePath,
} from "../../lib/proposalUploadMock";
import { ensureInvoiceFromAcceptedProposal } from "../../lib/proposalInvoice";
import AdminSideDrawer, { DrawerFormBody } from "../../components/admin/AdminSideDrawer";
import {
  STAGE_LABEL,
  isBarterClient,
  compensationExchangeLabel,
  isOutreachArchived,
  isProspectStage,
  normalizeEngagementStage,
  prospectScopeLabel,
  scopeReviewStatusLabel,
} from "../../lib/engagementStages";

const PROSPECT_STAGE_MENU = [
  { value: "outreach", label: "Inquiry" },
  { value: "met", label: "Met" },
  { value: "proposal", label: "Scope + Invoice" },
  { value: "archive", label: "Archived" },
];

function prospectStageMenuFor(client) {
  if (!isBarterClient(client)) return PROSPECT_STAGE_MENU;
  return PROSPECT_STAGE_MENU.map((opt) =>
    opt.value === "proposal" ? { ...opt, label: "Agreement" } : opt,
  );
}

function formatReviewStamp(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Long calendar date for document meta under the filename. */
function formatDocumentDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

const TYPE_LABEL = {
  brand_design: "Brand design",
  web_design: "Web design",
  product_design: "Product design",
  operational_ecosystem: "Operational ecosystem",
};

function parseEventDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtMeetingDay(iso) {
  const d = parseEventDate(iso);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function fmtMeetingTime(iso) {
  const d = parseEventDate(iso);
  if (!d) return "—";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function shortMeetingLabel(title) {
  const parts = String(title || "").split("—");
  return (parts[1] || "Meeting").trim();
}

function isMockMeetingId(id) {
  return String(id || "").startsWith("mock-");
}

/** Temporary meetings until Google / scheduled events exist for this prospect. */
function buildMockMeetings(companyName, clientId) {
  const company = (companyName || "Prospect").trim() || "Prospect";
  const now = new Date();
  const at = (dayOffset, hour, minute = 0) => {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + dayOffset);
    d.setHours(hour, minute, 0, 0);
    return d.toISOString();
  };
  const end = (dayOffset, hour, minute = 0) => {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + dayOffset);
    d.setHours(hour, minute, 0, 0);
    return d.toISOString();
  };

  return [
    {
      id: `mock-${clientId}-past-discovery`,
      client_id: clientId,
      title: `${company} x TAĪSTU — Discovery`,
      type: "call",
      start: at(-5, 11, 0),
      end: end(-5, 11, 45),
      meeting_notes: [
        "Inbound from their site. Pain: brand feels dated vs competitors. Budget band ~$18–25k. Decision maker is Maya (founder).",
        "",
        "Context",
        "— Current identity was DIY’d in 2021; packaging and web feel disconnected.",
        "— They’re pitching two retail partners in Q4 and want the system ready before samples ship.",
        "— Internal team is tiny (Maya + one ops lead). They’ll need us to own art direction.",
        "",
        "What landed",
        "— Liked the quieter end of our portfolio more than the high-contrast work.",
        "— Asked about a phased scope: identity first, then site, then packaging templates.",
        "— Open to a monthly ops retainer after launch if handoff is clean.",
        "",
        "Open questions",
        "— Do they have photography of the ceramics line, or do we need to plan a shoot?",
        "— Who else needs to sign off before invoice acceptance?",
        "— Preferred kickoff week if they move forward after scope walkthrough.",
        "",
        "Next",
        "Send a short recap + hold the scope walkthrough. Bring a one-page phase map and a clear accept / revise path.",
      ].join("\n"),
      location: "",
      source: "portal",
    },
    {
      id: `mock-${clientId}-upcoming-scope`,
      client_id: clientId,
      title: `${company} x TAĪSTU — Scope walkthrough`,
      type: "call",
      start: at(1, 14, 0),
      end: end(1, 15, 0),
      meeting_notes: [
        "Walk the scope packet + invoice. Confirm timeline for kickoff if they accept.",
        "",
        "Prep",
        "— Open with phase map (Identity → Site → Packaging templates).",
        "— Show sample invoice line items; keep language plain.",
        "— Have revise path ready if they want to drop packaging for v1.",
        "",
        "Watch for",
        "— Budget stretch if photography is required.",
        "— Decision latency — Maya may want 48h with her ops lead.",
      ].join("\n"),
      location: "Google Meet",
      source: "google",
    },
    {
      id: `mock-${clientId}-upcoming-checkin`,
      client_id: clientId,
      title: `${company} x TAĪSTU`,
      type: "call",
      start: at(4, 10, 30),
      end: end(4, 11, 0),
      meeting_notes: "",
      location: "",
      source: "portal",
    },
  ];
}

export default function ProspectRecordPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [meetingNotesSaveState, setMeetingNotesSaveState] = useState("idle");
  const [prospectNotesSaveState, setProspectNotesSaveState] = useState("idle");
  const [showInvoice, setShowInvoice] = useState(false);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [meetingNotesDraft, setMeetingNotesDraft] = useState("");
  const [stageMenuOpen, setStageMenuOpen] = useState(false);
  const [startEngagementBusy, setStartEngagementBusy] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [acceptMenuOpen, setAcceptMenuOpen] = useState(false);
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [uploadedProposal, setUploadedProposal] = useState(null);
  const [proposalActivityOpen, setProposalActivityOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [fieldsMode, setFieldsMode] = useState("one"); // one | multiple
  const [fieldsDraft, setFieldsDraft] = useState([]);
  const [fieldsBusy, setFieldsBusy] = useState(false);
  const [barterAgreement, setBarterAgreement] = useState(null);
  const [barterSignBusy, setBarterSignBusy] = useState(false);
  const [barterFieldsDraft, setBarterFieldsDraft] = useState(() =>
    normalizeBarterFields(MOCK_BARTER_AGREEMENT_FIELDS),
  );
  const meetingDraftRef = useRef("");
  const selectedMeetingIdRef = useRef(null);
  const meetingSaveTimerRef = useRef(null);
  const prospectNotesTimerRef = useRef(null);
  const prospectNotesRef = useRef("");
  const clientProspectNotesRef = useRef("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isLocalApp() && isMockBarterClientId(clientId)) {
        const mock = getMockBarterClient(clientId);
        setClient(mock);
        setNotes(mock?.prospect_notes || "");
        setUploadedProposal(null);
        setInvoices([]);
        setMeetings(buildMockMeetings(mock?.name, clientId));
        setBarterAgreement(
          mock?.barter_agreement ||
            (mock?.compensation_type === "barter"
              ? {
                  status: "draft",
                  fields: {
                    ...MOCK_BARTER_AGREEMENT_FIELDS,
                    PARTNER: mock.name,
                    FOUNDER: mock.primary_contact_name || "",
                  },
                }
              : null),
        );
        return;
      }
      const [clientsRes, invoicesRes, calendarRes] = await Promise.all([
        api.get("/admin/clients"),
        api.get("/admin/revenue/invoices").catch(() => ({ data: [] })),
        api.get("/portal/calendar", { params: { client_id: clientId } }).catch(() => ({ data: [] })),
      ]);
      const match = (clientsRes.data || []).find((c) => c.id === clientId) || null;
      setClient(match);
      setNotes(match?.prospect_notes || "");
      if (match && isBarterClient(match)) {
        try {
          const { data } = await api.get(`/admin/clients/${clientId}/barter-agreement`);
          const agreement = data?.barter_agreement || match.barter_agreement || null;
          setBarterAgreement(
            agreement
              ? {
                  ...agreement,
                  fields: seedBarterFieldsFromClient(agreement.fields, match),
                }
              : {
                  status: "draft",
                  fields: seedBarterFieldsFromClient({}, match),
                },
          );
        } catch {
          setBarterAgreement(
            match.barter_agreement
              ? {
                  ...match.barter_agreement,
                  fields: seedBarterFieldsFromClient(match.barter_agreement.fields, match),
                }
              : {
                  status: "draft",
                  fields: seedBarterFieldsFromClient({}, match),
                },
          );
        }
      } else {
        setBarterAgreement(null);
      }
      if (isLocalApp() && match) {
        // Local demo: seed a proposal if none exists yet.
        setUploadedProposal(ensureMockUploadedProposal(clientId, match.name, match.primary_contact_name));
      } else {
        setUploadedProposal(resolveUploadedProposal(clientId, match));
      }
      const rows = Array.isArray(invoicesRes.data)
        ? invoicesRes.data
        : invoicesRes.data?.invoices || [];
      setInvoices(rows.filter((inv) => (inv.clientId || inv.client_id) === clientId));
      const events = (Array.isArray(calendarRes.data) ? calendarRes.data : []).filter(
        (e) => e.type !== "milestone" && !String(e.id || "").startsWith("milestone-"),
      );
      setMeetings(
        events.length > 0
          ? events
          : isLocalApp()
            ? buildMockMeetings(match?.name, clientId)
            : [],
      );
    } catch {
      toast.error("Could not load prospect");
      setClient(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  // After local accept: draft invoice with option + selected add-on (cash only).
  // Skip when the server already created the invoice on accept.
  useEffect(() => {
    if (!client || !uploadedProposal) return;
    if (isBarterClient(client)) return;
    if (uploadedProposal.status !== "accepted") return;
    if (uploadedProposal.revenueInvoiceId) return;
    let cancelled = false;
    (async () => {
      try {
        const invoice = await ensureInvoiceFromAcceptedProposal(uploadedProposal, client);
        if (cancelled || !invoice) return;
        const invoiceId = invoice.id || invoice.invoiceId;
        if (invoiceId && invoiceId !== uploadedProposal.revenueInvoiceId) {
          setUploadedProposal(setUploadedProposalInvoiceId(clientId, invoiceId));
        }
        // Refresh invoices list so the new draft shows.
        const { data } = await api.get("/admin/revenue/invoices").catch(() => ({ data: [] }));
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : data?.invoices || [];
        setInvoices(rows.filter((inv) => (inv.clientId || inv.client_id) === clientId));
      } catch {
        // Non-blocking — studio can still create manually.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    client,
    clientId,
    uploadedProposal?.status,
    uploadedProposal?.acceptedOptionIndex,
    uploadedProposal?.acceptedAddon?.title,
    uploadedProposal?.acceptedAddon?.amount,
    uploadedProposal?.revenueInvoiceId,
  ]);

  const stage = useMemo(
    () => normalizeEngagementStage(client?.engagement_stage, { defaultStage: "outreach" }),
    [client],
  );

  const sortedMeetings = useMemo(() => {
    const now = Date.now();
    return [...meetings].sort((a, b) => {
      const aStart = parseEventDate(a.start)?.getTime() ?? 0;
      const bStart = parseEventDate(b.start)?.getTime() ?? 0;
      const aUpcoming = aStart >= now;
      const bUpcoming = bStart >= now;
      if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1;
      return aUpcoming ? aStart - bStart : bStart - aStart;
    });
  }, [meetings]);

  const selectedMeeting = useMemo(
    () => sortedMeetings.find((m) => m.id === selectedMeetingId) || null,
    [sortedMeetings, selectedMeetingId],
  );

  useEffect(() => {
    if (!sortedMeetings.length) {
      setSelectedMeetingId(null);
      setMeetingNotesDraft("");
      return;
    }
    if (!selectedMeetingId || !sortedMeetings.some((m) => m.id === selectedMeetingId)) {
      const now = Date.now();
      const nextUp =
        sortedMeetings.find((m) => (parseEventDate(m.start)?.getTime() ?? 0) >= now) ||
        sortedMeetings[0];
      setSelectedMeetingId(nextUp.id);
      setMeetingNotesDraft(nextUp.meeting_notes || "");
    }
  }, [sortedMeetings, selectedMeetingId]);

  useEffect(() => {
    if (selectedMeeting) {
      setMeetingNotesDraft(selectedMeeting.meeting_notes || "");
      setMeetingNotesSaveState("idle");
    }
  }, [selectedMeeting?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    meetingDraftRef.current = meetingNotesDraft;
  }, [meetingNotesDraft]);

  useEffect(() => {
    selectedMeetingIdRef.current = selectedMeetingId;
  }, [selectedMeetingId]);

  useEffect(() => {
    prospectNotesRef.current = notes;
  }, [notes]);

  useEffect(() => {
    clientProspectNotesRef.current = client?.prospect_notes || "";
  }, [client?.prospect_notes]);

  const persistMeetingNotes = useCallback(
    async (meetingId, text) => {
      if (!clientId || !meetingId) return false;
      if (isMockMeetingId(meetingId)) {
        setMeetings((prev) =>
          prev.map((m) => (m.id === meetingId ? { ...m, meeting_notes: text || "" } : m)),
        );
        setMeetingNotesSaveState("saved");
        return true;
      }
      setMeetingNotesSaveState("saving");
      try {
        await api.patch(
          `/portal/calendar/${meetingId}`,
          { meeting_notes: text || "" },
          { params: { client_id: clientId } },
        );
        setMeetings((prev) =>
          prev.map((m) => (m.id === meetingId ? { ...m, meeting_notes: text || "" } : m)),
        );
        setMeetingNotesSaveState("saved");
        return true;
      } catch (err) {
        setMeetingNotesSaveState("error");
        toast.error(err.response?.data?.detail || "Could not save meeting notes");
        return false;
      }
    },
    [clientId],
  );

  const persistProspectNotes = useCallback(
    async (text) => {
      if (!clientId) return false;
      setProspectNotesSaveState("saving");
      try {
        await api.patch(`/admin/clients/${clientId}/prospect-notes`, {
          prospect_notes: text || "",
        });
        setClient((prev) => (prev ? { ...prev, prospect_notes: text || "" } : prev));
        clientProspectNotesRef.current = text || "";
        setProspectNotesSaveState("saved");
        return true;
      } catch (err) {
        setProspectNotesSaveState("error");
        toast.error(err.response?.data?.detail || "Could not save prospect notes");
        return false;
      }
    },
    [clientId],
  );

  const flushMeetingNotes = useCallback(async () => {
    if (meetingSaveTimerRef.current) {
      window.clearTimeout(meetingSaveTimerRef.current);
      meetingSaveTimerRef.current = null;
    }
    const meetingId = selectedMeetingIdRef.current;
    if (!clientId || !meetingId) return;
    const text = meetingDraftRef.current || "";
    const current = meetings.find((m) => m.id === meetingId);
    if (!current || (current.meeting_notes || "") === text) return;
    await persistMeetingNotes(meetingId, text);
  }, [clientId, meetings, persistMeetingNotes]);

  // Autosave per-meeting notes.
  useEffect(() => {
    if (!clientId || !selectedMeetingId || !selectedMeeting) return undefined;
    if ((meetingNotesDraft || "") === (selectedMeeting.meeting_notes || "")) return undefined;
    if (meetingSaveTimerRef.current) window.clearTimeout(meetingSaveTimerRef.current);
    meetingSaveTimerRef.current = window.setTimeout(() => {
      meetingSaveTimerRef.current = null;
      persistMeetingNotes(selectedMeetingId, meetingNotesDraft || "");
    }, 650);
    return () => {
      if (meetingSaveTimerRef.current) {
        window.clearTimeout(meetingSaveTimerRef.current);
        meetingSaveTimerRef.current = null;
      }
    };
  }, [
    clientId,
    selectedMeetingId,
    meetingNotesDraft,
    selectedMeeting?.meeting_notes,
    persistMeetingNotes,
  ]);

  // Autosave prospect notes.
  useEffect(() => {
    if (!clientId || !client) return undefined;
    if ((notes || "") === (client.prospect_notes || "")) return undefined;
    if (prospectNotesTimerRef.current) window.clearTimeout(prospectNotesTimerRef.current);
    prospectNotesTimerRef.current = window.setTimeout(() => {
      prospectNotesTimerRef.current = null;
      persistProspectNotes(notes || "");
    }, 650);
    return () => {
      if (prospectNotesTimerRef.current) {
        window.clearTimeout(prospectNotesTimerRef.current);
        prospectNotesTimerRef.current = null;
      }
    };
  }, [clientId, client, notes, persistProspectNotes]);

  const recordStage = useMemo(
    () => normalizeEngagementStage(client?.engagement_stage, { defaultStage: "outreach" }),
    [client],
  );

  const barterAgreementPending =
    Boolean(client) &&
    isBarterClient(client) &&
    (barterAgreement?.status || client?.barter_agreement?.status || "draft") !== "signed";

  if (
    !loading &&
    client &&
    (recordStage === "active" || recordStage === "closeout") &&
    !barterAgreementPending
  ) {
    return <Navigate to="/admin/engagements" replace />;
  }

  const setStage = async (next) => {
    try {
      await api.patch(`/admin/clients/${clientId}/engagement-stage`, { engagement_stage: next });
      toast.success(`Stage: ${STAGE_LABEL[next] || next}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update stage");
    }
  };

  const startEngagement = async () => {
    if (!client || startEngagementBusy) return;
    if (isMockBarterClientId(clientId)) {
      toast.message("Local mock — start engagement is not available.");
      return;
    }
    const waitingFor = isBarterClient(client)
      ? "a signed agreement"
      : "a signed proposal and paid invoice";
    const ok = window.confirm(
      `Start engagement for ${client.name}?\n\nThis creates their Studio workspace now without waiting for ${waitingFor}. You can still finish paperwork later — other prospects still auto-start when they sign and the first invoice is paid.`,
    );
    if (!ok) return;
    setStartEngagementBusy(true);
    try {
      await api.patch(`/admin/clients/${clientId}/engagement-stage`, { engagement_stage: "active" });
      toast.success("Engagement started — opening workspace");
      navigate(portalBasePath(client));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start engagement");
    } finally {
      setStartEngagementBusy(false);
    }
  };

  const selectMeeting = async (meeting) => {
    if (meeting.id === selectedMeetingId) return;
    await flushMeetingNotes();
    setSelectedMeetingId(meeting.id);
    setMeetingNotesDraft(meeting.meeting_notes || "");
    setMeetingNotesSaveState("idle");
  };

  if (loading) {
    return (
      <div className="p-8 md:p-12" data-testid="prospect-record-page">
        <p className="t-meta">Loading prospect…</p>
      </div>
    );
  }

  const canViewRecord =
    client &&
    (isProspectStage(client.engagement_stage) ||
      recordStage === "archive" ||
      isOutreachArchived(client) ||
      barterAgreementPending);

  if (!canViewRecord) {
    return (
      <div className="p-8 md:p-12" data-testid="prospect-record-page">
        <p className="t-meta mb-4">Prospect not found.</p>
        <Link to="/admin/outreach" className="text-[var(--t-accent)] text-sm">
          Back to outreach
        </Link>
      </div>
    );
  }

  const now = Date.now();
  const stageSelectValue =
    recordStage === "archive" || isOutreachArchived(client)
      ? "archive"
      : stage === "invoice"
        ? "proposal"
        : stage;
  const stagePillLabel =
    prospectStageMenuFor(client).find((o) => o.value === stageSelectValue)?.label ||
    (stageSelectValue === "proposal" ? prospectScopeLabel(client) : null) ||
    STAGE_LABEL[stageSelectValue] ||
    "Inquiry";
  const scopeReview = client.scope_review || null;
  const isBarter = isBarterClient(client);
  const docLabel = isBarter ? "Agreement" : "Proposal";
  const proposalOptionsForAccept =
    uploadedProposal?.options?.length
      ? uploadedProposal.options
      : Array.isArray(client.proposal_options)
        ? client.proposal_options
        : [];
  const canMarkProposalAccepted =
    !isBarter &&
    scopeReview?.status !== "accepted" &&
    uploadedProposal?.status !== "accepted" &&
    (proposalOptionsForAccept.length > 0 ||
      Boolean(scopeReview?.packet_file_id) ||
      Boolean(scopeReview?.shared_at) ||
      Boolean(uploadedProposal));

  const setCompensationType = async (next) => {
    const value = next === "barter" ? "barter" : "cash";
    if ((client.compensation_type || "cash") === value) return;
    if (isMockBarterClientId(clientId)) {
      setClient((prev) => (prev ? { ...prev, compensation_type: value } : prev));
      toast.message("Local mock — exchange toggled in this session only.");
      return;
    }
    try {
      await api.patch(`/admin/clients/${clientId}`, { compensation_type: value });
      setClient((prev) => (prev ? { ...prev, compensation_type: value } : prev));
      toast.success(
        value === "barter"
          ? "Marked as barter (no invoice path)"
          : "Marked as invoice",
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update exchange type");
    }
  };

  const ensureScopeReview = async ({ rotate = false } = {}) => {
    const { data } = await api.post(`/admin/clients/${clientId}/scope-review`, {
      rotate_token: rotate,
    });
    setClient((prev) =>
      prev
        ? {
            ...prev,
            scope_review: data?.scope_review || prev.scope_review,
            engagement_stage: data?.engagement_stage || prev.engagement_stage,
          }
        : prev,
    );
    return data;
  };

  const refreshInvoicesForClient = async () => {
    const { data } = await api.get("/admin/revenue/invoices").catch(() => ({ data: [] }));
    const rows = Array.isArray(data) ? data : data?.invoices || [];
    setInvoices(rows.filter((inv) => (inv.clientId || inv.client_id) === clientId));
  };

  const markProposalAccepted = async (optionIndex = null, { addonSelected = false } = {}) => {
    if (!client || isBarter || acceptBusy) return;
    const options =
      uploadedProposal?.options?.length
        ? uploadedProposal.options
        : Array.isArray(client.proposal_options)
          ? client.proposal_options
          : [];
    const alreadyAccepted =
      scopeReview?.status === "accepted" || uploadedProposal?.status === "accepted";
    if (alreadyAccepted) {
      toast.message("Already marked accepted");
      return;
    }
    if (!options.length && !scopeReview?.packet_file_id && !scopeReview?.shared_at) {
      toast.error("Save a proposal before marking it accepted");
      return;
    }

    let idx = optionIndex;
    if (idx == null && options.length === 1) idx = 0;
    if (idx == null && options.length > 1) {
      setAcceptMenuOpen(true);
      return;
    }

    const chosen = idx != null && options[idx] ? options[idx] : null;
    const label = chosen?.option_label || (idx != null ? `Option ${idx + 1}` : null);

    setAcceptBusy(true);
    setAcceptMenuOpen(false);
    try {
      const { data } = await api.post(`/admin/clients/${clientId}/scope-review/accept`, {
        option_index: idx,
        option_label: label,
        addon_selected: Boolean(addonSelected),
        accepted_addon:
          addonSelected && chosen?.addon
            ? {
                title: chosen.addon.title,
                description: chosen.addon.description,
                amount: chosen.addon.amount,
              }
            : null,
        note: "Approved offline",
      });
      const nextReview = data?.scope_review || data?.client?.scope_review;
      const nextStage = data?.engagement_stage || data?.client?.engagement_stage;
      setClient((prev) =>
        prev
          ? {
              ...prev,
              scope_review: nextReview || prev.scope_review,
              engagement_stage: nextStage || prev.engagement_stage,
              status: data?.client?.status ?? prev.status,
              archived: data?.client?.archived ?? prev.archived,
            }
          : prev,
      );
      const merged = {
        ...client,
        scope_review: nextReview || client.scope_review,
        engagement_stage: nextStage || client.engagement_stage,
      };
      setUploadedProposal(resolveUploadedProposal(clientId, merged));
      await refreshInvoicesForClient();
      toast.success(
        data?.invoice_number
          ? `Accepted — invoice ${data.invoice_number} created`
          : "Marked accepted",
      );
    } catch (err) {
      // Local demo fallback when API isn’t available yet.
      if (isLocalApp() && uploadedProposal) {
        const row = acceptUploadedProposal(
          clientId,
          idx ?? 0,
          "Approved offline",
          Boolean(addonSelected),
        );
        if (row) {
          setUploadedProposal(row);
          setClient((prev) =>
            prev
              ? {
                  ...prev,
                  scope_review: {
                    ...(prev.scope_review || {}),
                    status: "accepted",
                    accepted_at: row.acceptedAt,
                    accepted_option_index: row.acceptedOptionIndex,
                    accepted_option_label: row.acceptedOptionLabel,
                    accepted_addon: row.acceptedAddon,
                    response_note: row.responseNote,
                  },
                  engagement_stage: "invoice",
                }
              : prev,
          );
          toast.success("Marked accepted — drafting invoice");
          return;
        }
      }
      toast.error(formatApiDetail(err.response?.data?.detail || err.message, "Could not mark accepted"));
    } finally {
      setAcceptBusy(false);
    }
  };

  const shareBarterAgreementLink = async () => {
    if (shareBusy) return;
    if (isMockBarterClientId(clientId)) {
      const path = MOCK_BARTER_AGREEMENT_PATH;
      try {
        await navigator.clipboard.writeText(`${window.location.origin}${path}`);
        toast.success("Client agreement link copied");
      } catch {
        toast.message(path);
      }
      setShareMenuOpen(false);
      return;
    }
    setShareBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${clientId}/barter-agreement/share`);
      setBarterAgreement(data?.barter_agreement || null);
      setClient((prev) =>
        prev
          ? {
              ...prev,
              barter_agreement: data?.barter_agreement || prev.barter_agreement,
              engagement_stage: data?.engagement_stage || "proposal",
            }
          : prev,
      );
      const path =
        data?.share_path ||
        agreementPreviewPath(client) ||
        `/agreement/${data?.barter_agreement?.token}`;
      await navigator.clipboard.writeText(`${window.location.origin}${path}`);
      toast.success("Agreement link copied");
      setShareMenuOpen(false);
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Could not share agreement"));
    } finally {
      setShareBusy(false);
    }
  };

  const countersignBarterAgreement = async (stroke) => {
    if (!stroke || barterSignBusy) return;
    if (isMockBarterClientId(clientId)) {
      const stamp = new Date().toISOString();
      setBarterAgreement((prev) => ({
        ...(prev || {}),
        status: "signed",
        studio_signature: stroke,
        studio_signed_at: stamp,
        signed_at: stamp,
      }));
      toast.success("Local mock — signed (email skipped).");
      return;
    }
    setBarterSignBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${clientId}/barter-agreement/sign`, {
        signature: stroke,
      });
      setBarterAgreement(data?.barter_agreement || data || null);
      toast.success("Signed — copy emailed to the partner.");
      await load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, "Could not countersign"));
    } finally {
      setBarterSignBusy(false);
    }
  };

  const shareDownload = async () => {
    if (shareBusy) return;
    setShareBusy(true);
    try {
      const data = await ensureScopeReview();
      const fileId = data?.scope_review?.packet_file_id;
      if (!fileId) throw new Error("No scope packet");
      await downloadPacketByFileId(fileId, `${client?.name || "prospect"}-scope.pdf`);
      toast.success("Downloaded");
      setShareMenuOpen(false);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || err.message || "Save a scope packet to Library first.",
      );
    } finally {
      setShareBusy(false);
    }
  };

  const shareEmail = async () => {
    if (shareBusy) return;
    const toEmail = (client?.primary_contact_email || "").trim();
    if (!toEmail) {
      toast.error("Add a contact email first");
      return;
    }
    setShareBusy(true);
    try {
      const data = await ensureScopeReview();
      const fileId = data?.scope_review?.packet_file_id;
      if (!fileId) throw new Error("No scope packet");
      const sent = await sendPacket({ fileId, toEmail });
      toast.success(sent?.to_email ? `Sent to ${sent.to_email}` : "Email sent");
      setShareMenuOpen(false);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || err.message || "Could not email scope packet.",
      );
    } finally {
      setShareBusy(false);
    }
  };

  const refreshUploaded = () => {
    load();
  };

  const openFieldsEditor = () => {
    if (isBarter) {
      const seeded = seedBarterFieldsFromClient(
        barterAgreement?.fields ||
          (clientId === MOCK_BARTER_AGREEMENT_ID || isMockBarterClientId(clientId)
            ? MOCK_BARTER_AGREEMENT_FIELDS
            : {}),
        client,
      );
      setBarterFieldsDraft(seeded);
      setFieldsOpen(true);
      return;
    }
    const emptyOpt = () => ({
      option_label: "",
      intro: "",
      timeline: PROPOSAL_TIMELINES[0],
      investment_total: "",
      exchange: "",
      terms: "",
      scope_items: [{ title: "", description: "" }],
      addon_enabled: false,
      addon_title: "",
      addon_description: "",
      addon_amount: "",
    });
    const existing = proposalOptionsDraft(client, uploadedProposal) || [emptyOpt()];
    const multi = existing.length > 1;
    setFieldsMode(!multi ? "one" : "multiple");
    setFieldsDraft(!multi ? existing.slice(0, 1) : existing);
    setFieldsOpen(true);
  };

  const setProposalFieldsMode = (mode) => {
    const emptyOpt = () => ({
      option_label: "",
      intro: "",
      timeline: PROPOSAL_TIMELINES[0],
      investment_total: "",
      scope_items: [{ title: "", description: "" }],
      addon_enabled: false,
      addon_title: "",
      addon_description: "",
      addon_amount: "",
    });
    setFieldsMode(mode);
    if (mode === "one") {
      setFieldsDraft((prev) => (prev.length ? [prev[0]] : [emptyOpt()]));
    } else {
      setFieldsDraft((prev) => (prev.length ? prev : [emptyOpt()]));
    }
  };

  const publishProposalOptions = async (options) => {
    const { data } = await api.put(`/admin/clients/${clientId}/proposal-options`, {
      options: optionsPayloadForApi(options),
    });
    return data;
  };

  const saveBarterFieldsEditor = async () => {
    const fields = seedBarterFieldsFromClient(barterFieldsDraft, client);
    if (!fields.PARTNER || !fields.FOUNDER) {
      toast.error("Partner (company) and Founder are required.");
      return;
    }
    setFieldsBusy(true);
    try {
      if (isMockBarterClientId(clientId)) {
        setBarterAgreement((prev) => ({
          ...(prev || { status: "draft" }),
          fields,
        }));
        setFieldsOpen(false);
        toast.success("Agreement updated");
        return;
      }
      if (barterAgreement?.status === "signed") {
        throw new Error("Signed agreements can’t be edited");
      }
      const { data } = await api.put(`/admin/clients/${clientId}/barter-agreement`, { fields });
      setBarterAgreement(data?.barter_agreement || { ...(barterAgreement || {}), fields });
      setFieldsOpen(false);
      toast.success("Agreement updated");
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail || err.message, "Could not save"));
    } finally {
      setFieldsBusy(false);
    }
  };

  const saveFieldsEditor = async () => {
    if (isBarter) {
      await saveBarterFieldsEditor();
      return;
    }
    setFieldsBusy(true);
    try {
      const toSave =
        fieldsMode === "one" ? fieldsDraft.slice(0, 1) : fieldsDraft;
      const apiOptions = optionsPayloadForApi(
        toSave.map((o, i) =>
          normalizeProposalOption(
            {
              ...o,
              addon:
                o.addon_enabled === false
                  ? null
                  : o.addon ||
                    (o.addon_title || o.addon_description || o.addon_amount
                      ? {
                          title: o.addon_title,
                          description: o.addon_description,
                          amount: o.addon_amount,
                        }
                      : null),
            },
            client?.name,
            i,
          ),
        ).filter(
          (o) => o.option_label || o.intro || o.scope_items.some((s) => s.title),
        ),
      );
      if (!apiOptions.length) {
        throw new Error("Add at least one option");
      }
      await publishProposalOptions(apiOptions);
      const row = saveProposalFromFields(
        clientId,
        client?.name,
        toSave,
        client?.primary_contact_name,
      );
      setUploadedProposal(row);
      setFieldsOpen(false);
      toast.success(
        fieldsMode === "one"
          ? "Proposal saved — public link is live"
          : "Proposal options saved — public link is live",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not save options");
    } finally {
      setFieldsBusy(false);
    }
  };

  const updateFieldOption = (idx, patch) => {
    setFieldsDraft((prev) => prev.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  };

  const updateFieldScopeItem = (optIdx, itemIdx, patch) => {
    setFieldsDraft((prev) =>
      prev.map((o, i) => {
        if (i !== optIdx) return o;
        const scope_items = (o.scope_items || []).map((s, j) =>
          j === itemIdx ? { ...s, ...patch } : s,
        );
        return { ...o, scope_items };
      }),
    );
  };

  const copyUploadShareLink = async () => {
    if (!uploadedProposal) {
      toast.error("Save a proposal first");
      return;
    }
    setShareBusy(true);
    try {
      await publishProposalOptions(uploadedProposal.options);
      const data = await ensureScopeReview();
      const next = markProposalShared(clientId);
      setUploadedProposal(next);
      const path =
        data?.share_path || uploadedProposalSharePath(client || uploadedProposal);
      const url = `${window.location.origin}${path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Review link copied");
      } catch {
        toast.message(url);
      }
      setShareMenuOpen(false);
    } catch (err) {
      toast.error(
        err.response?.data?.detail || err.message || "Could not create share link",
      );
    } finally {
      setShareBusy(false);
    }
  };

  /** Studio opens the proposal document (prospect cannot be impersonated for accept). */
  const openProposalFile = () => {
    if (uploadedProposal) {
      // Clear a prior accept so opening the file always shows the proposal + add-on.
      const cleared = clearUploadedProposalDecision(clientId);
      if (cleared) setUploadedProposal(cleared);
      openAppPathInNewTab(uploadedProposalSharePath(client || cleared || uploadedProposal));
      return;
    }
    const slug = String(client?.slug || "").replace(/-/g, "") || null;
    if (slug) {
      openAppPathInNewTab(`/${slug}/proposal`);
    } else {
      toast.message("Share a proposal link first");
    }
  };

  const openBarterAgreementFile = () => {
    if (isMockBarterClientId(clientId)) {
      openAppPathInNewTab(MOCK_BARTER_AGREEMENT_STUDIO_PATH);
      return;
    }
    const slug = clientSlug(client);
    if (slug) {
      openAppPathInNewTab(agreementPreviewPath(client));
      return;
    }
    openAppPathInNewTab(`/agreement/preview/${clientId}`);
  };

  const barterFields = seedBarterFieldsFromClient(
    barterAgreement?.fields ||
      (clientId === MOCK_BARTER_AGREEMENT_ID || isMockBarterClientId(clientId)
        ? MOCK_BARTER_AGREEMENT_FIELDS
        : {}),
    client,
  );
  const barterFileLabel = barterAgreementFileName(barterFields);
  const barterStatusText = barterAgreementStatusLabel(barterAgreement?.status);
  const canCountersignBarter =
    barterAgreement?.status === "partner_signed" ||
    (isMockBarterClientId(clientId) &&
      clientId === MOCK_BARTER_AGREEMENT_ID &&
      barterAgreement?.status !== "signed" &&
      !barterAgreement?.studio_signature);

  return (
    <div className="min-h-0 max-w-full overflow-x-clip p-4 sm:p-5 md:p-7" data-testid="prospect-record-page">
      <button
        type="button"
        onClick={() => navigate("/admin/outreach")}
        className="mb-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-accent)]"
        data-testid="prospect-back"
      >
        <ArrowLeft size={14} strokeWidth={1.4} /> Outreach
      </button>

      {isMockBarterClientId(clientId) ? (
        <p
          className="mb-4 text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]"
          data-testid="prospect-mock-barter-banner"
        >
          Local mock · barter
          {clientId === MOCK_BARTER_AGREEMENT_ID ? (
            <>
              {" · "}
              <button
                type="button"
                className="underline"
                onClick={() => openAppPathInNewTab(MOCK_BARTER_AGREEMENT_PATH)}
              >
                Client view
              </button>
              {" · "}
              <button
                type="button"
                className="underline"
                onClick={() => openAppPathInNewTab(MOCK_BARTER_AGREEMENT_STUDIO_PATH)}
              >
                Studio preview
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      <header className="mb-6 sm:mb-8">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl tracking-tight !font-extralight md:text-3xl">{client.name}</h1>
            <p className="mt-1 text-sm text-[var(--t-meta)]">
              {client.primary_contact_name || "—"}
              {client.primary_contact_email ? ` · ${client.primary_contact_email}` : ""}
            </p>
          </div>
          {(isProspectStage(client.engagement_stage) ||
            recordStage === "archive" ||
            isOutreachArchived(client)) && (
            <button
              type="button"
              onClick={startEngagement}
              disabled={startEngagementBusy}
              className="neo-cta self-start shrink-0 disabled:opacity-50"
              data-testid="prospect-start-engagement"
            >
              {startEngagementBusy ? "Starting…" : "Start Engagement"}
            </button>
          )}
        </div>
        {barterAgreementPending && (recordStage === "active" || recordStage === "closeout") ? (
          <p className="mb-4 text-sm text-[var(--t-meta)]" data-testid="prospect-active-awaiting-agreement">
            Engagement is active — agreement still needs both signatures. Once signed, the PDF lands in Library.
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill
            status={client.service_tier}
            label={TYPE_LABEL[client.service_tier]}
            className={STATUS_PILL_ROW_COMPACT_CLASS}
          />
          <button
            type="button"
            onClick={() => setCompensationType(isBarter ? "cash" : "barter")}
            className={cn(
              "t-pill inline-flex cursor-pointer border bg-transparent text-left font-[inherit] hover:opacity-90",
              STATUS_PILL_ROW_COMPACT_CLASS,
              isBarter ? "t-pill-accent" : "t-pill-meta",
            )}
            data-testid="prospect-compensation-toggle"
            title="Toggle in-kind vs invoice exchange"
          >
            {compensationExchangeLabel(client.compensation_type)}
          </button>
          {scopeReview?.status === "accepted" ? (
            <span
              className={cn("t-pill t-pill-accent", STATUS_PILL_ROW_COMPACT_CLASS)}
              data-testid="prospect-scope-accepted-pill"
            >
              {isBarter ? "Agreement accepted" : "Scope accepted"}
              {scopeReview.accepted_option_label ? (
                <span className="hidden sm:inline">{` · ${scopeReview.accepted_option_label}`}</span>
              ) : null}
            </span>
          ) : scopeReview?.status === "changes_requested" ? (
            <span
              className={cn("t-pill t-pill-warn", STATUS_PILL_ROW_COMPACT_CLASS)}
              data-testid="prospect-scope-changes-pill"
            >
              Changes requested
            </span>
          ) : scopeReview?.status === "pending" ? (
            <span
              className={cn("t-pill t-pill-pink", STATUS_PILL_ROW_COMPACT_CLASS)}
              data-testid="prospect-scope-pending-pill"
            >
              {isBarter ? "Awaiting agreement" : "Awaiting response"}
            </span>
          ) : null}
          <Popover open={stageMenuOpen} onOpenChange={setStageMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "t-pill t-pill-accent inline-flex cursor-pointer border bg-transparent text-left font-[inherit] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--t-bg)]",
                  STATUS_PILL_ROW_COMPACT_CLASS,
                )}
                data-testid="prospect-stage-select"
                aria-label="Change prospect status"
              >
                {stagePillLabel}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={6}
              className="z-[60] w-auto min-w-[12rem] neo-panel p-2 text-[var(--t-text)] t-glass"
              data-testid="prospect-stage-menu"
            >
              <div className="flex flex-col gap-1">
                {prospectStageMenuFor(client).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={cn(
                      "rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]",
                      opt.value === stageSelectValue &&
                        "border border-[var(--t-accent)] bg-[var(--t-accent)]/5 text-[var(--t-accent)]",
                    )}
                    onClick={() => {
                      setStageMenuOpen(false);
                      if (opt.value !== stageSelectValue) setStage(opt.value);
                    }}
                    data-testid={`prospect-stage-option-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </header>

      <div className="space-y-3 sm:space-y-4 md:space-y-5">
        <section className="neo-panel p-5 md:p-6" data-testid="prospect-meetings">
          <div className="label-eyebrow mb-3">Meeting</div>
          {selectedMeeting ? (
            <div className="mb-5">
              <div className="mb-2 flex items-start gap-2">
                <Calendar size={16} strokeWidth={1.4} className="mt-0.5 shrink-0 text-[var(--t-accent)]" />
                <div className="font-heading text-lg tracking-tight !font-light leading-snug">
                  {shortMeetingLabel(selectedMeeting.title)}
                </div>
              </div>
              <div className="t-meta pl-6">
                {fmtMeetingDay(selectedMeeting.start)} · {fmtMeetingTime(selectedMeeting.start)}
              </div>
            </div>
          ) : (
            <div className="t-meta mb-5">No upcoming meeting scheduled.</div>
          )}

          {sortedMeetings.length > 1 ? (
            <div className="mb-5 border-t border-black/5 pt-3" data-testid="prospect-meetings-list">
              <ul className="divide-y divide-black/5">
                {sortedMeetings.map((m) => {
                  const active = m.id === selectedMeetingId;
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => selectMeeting(m)}
                        className={`flex w-full items-baseline justify-between gap-3 py-2.5 text-left ${
                          active ? "text-[var(--t-text)]" : "text-[var(--t-meta)]"
                        }`}
                        data-testid={`prospect-meeting-${m.id}`}
                      >
                        <span className="font-heading min-w-0 truncate text-sm !font-light">
                          {shortMeetingLabel(m.title)}
                        </span>
                        <span className="shrink-0 text-[11px] tabular-nums">
                          {fmtMeetingDay(m.start)} · {fmtMeetingTime(m.start)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          <div data-testid="prospect-meeting-notes">
            {selectedMeeting ? (
              <>
                <div className="mb-2 flex items-center justify-end">
                  {meetingNotesSaveState !== "idle" ? (
                    <span
                      className={`text-[11px] tracking-[0.04em] ${
                        meetingNotesSaveState === "error"
                          ? "text-[var(--t-error)]"
                          : "text-[var(--t-meta)]"
                      }`}
                      data-testid="prospect-meeting-notes-save-status"
                    >
                      {meetingNotesSaveState === "saving"
                        ? "Saving…"
                        : meetingNotesSaveState === "saved"
                          ? "Saved"
                          : "Couldn’t save"}
                    </span>
                  ) : null}
                </div>
                <textarea
                  className="t-scroll-soft min-h-[140px] w-full resize-none border-0 bg-transparent p-0 text-sm leading-relaxed outline-none placeholder:text-[var(--t-meta)]/55"
                  value={meetingNotesDraft}
                  onChange={(e) => setMeetingNotesDraft(e.target.value)}
                  placeholder="Soft space for what mattered on this call…"
                  data-testid="prospect-meeting-notes-input"
                />
              </>
            ) : null}
          </div>
        </section>

        <section className="neo-panel p-5 md:p-6" data-testid={isBarter ? "prospect-barter-agreement" : "prospect-scope-review"}>
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div className="label-eyebrow mb-0">{docLabel}</div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)] disabled:opacity-40"
                onClick={openFieldsEditor}
                disabled={isBarter && barterAgreement?.status === "signed"}
                data-testid="prospect-edit-proposal-fields"
              >
                Edit fields
              </button>
              {canMarkProposalAccepted ? (
                proposalOptionsForAccept.length > 1 ? (
                  <Popover open={acceptMenuOpen} onOpenChange={setAcceptMenuOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)] disabled:opacity-40"
                        disabled={acceptBusy}
                        data-testid="prospect-mark-accepted"
                      >
                        {acceptBusy ? "Accepting…" : "Mark accepted"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="end"
                      sideOffset={6}
                      className="z-[60] w-auto min-w-[12rem] neo-panel p-2 text-[var(--t-text)] t-glass"
                      data-testid="prospect-mark-accepted-menu"
                    >
                      <p className="px-3 pb-1 pt-1 text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)]">
                        Which option?
                      </p>
                      <div className="flex flex-col gap-1">
                        {proposalOptionsForAccept.map((opt, idx) => (
                          <button
                            key={opt.option_label || idx}
                            type="button"
                            className="rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]"
                            onClick={() => markProposalAccepted(idx)}
                            data-testid={`prospect-mark-accepted-option-${idx}`}
                          >
                            {opt.option_label || `Option ${idx + 1}`}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <button
                    type="button"
                    className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)] disabled:opacity-40"
                    onClick={() => markProposalAccepted(proposalOptionsForAccept.length === 1 ? 0 : null)}
                    disabled={acceptBusy}
                    data-testid="prospect-mark-accepted"
                  >
                    {acceptBusy ? "Accepting…" : "Mark accepted"}
                  </button>
                )
              ) : null}
              <Popover open={shareMenuOpen} onOpenChange={setShareMenuOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]"
                    data-testid="prospect-share-scope"
                    disabled={shareBusy}
                  >
                    {shareBusy ? "Sharing…" : "Share"}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  sideOffset={6}
                  className="z-[60] w-auto min-w-[10rem] neo-panel p-2 text-[var(--t-text)] t-glass"
                  data-testid="prospect-share-menu"
                >
                  <div className="flex flex-col gap-1">
                    {isBarter ? (
                      <button
                        type="button"
                        className="rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]"
                        onClick={shareBarterAgreementLink}
                        data-testid="prospect-share-copy-link"
                      >
                        Copy review link
                      </button>
                    ) : null}
                    {!isBarter && uploadedProposal ? (
                      <button
                        type="button"
                        className="rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]"
                        onClick={copyUploadShareLink}
                        data-testid="prospect-share-copy-link"
                      >
                        Copy review link
                      </button>
                    ) : null}
                    {!isBarter ? (
                      <>
                        <button
                          type="button"
                          className="rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]"
                          onClick={shareDownload}
                          disabled={shareBusy}
                          data-testid="prospect-share-download"
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          className="rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]"
                          onClick={shareEmail}
                          disabled={shareBusy}
                          data-testid="prospect-share-email"
                        >
                          Email
                        </button>
                      </>
                    ) : null}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {isBarter ? (
            <div className="space-y-4" data-testid="prospect-barter-file">
              <div>
                <div className="t-meta">{barterStatusText}</div>
                <button
                  type="button"
                  onClick={openBarterAgreementFile}
                  className="mt-1 block text-left font-heading text-base !font-light tracking-tight hover:text-[var(--t-accent)]"
                  data-testid="prospect-barter-open-file"
                >
                  {barterFileLabel}
                </button>
                {formatBarterStartDate(barterFields.START_DATE) ? (
                  <p className="mt-0.5 text-xs text-[var(--t-meta)]">
                    {formatBarterStartDate(barterFields.START_DATE)}
                  </p>
                ) : null}
              </div>
              {barterAgreement?.partner_signed_at || barterAgreement?.studio_signed_at ? (
                <div className="space-y-1 text-xs text-[var(--t-meta)]">
                  {barterAgreement?.partner_signed_at ? (
                    <p>
                      Partner signed {formatReviewStamp(barterAgreement.partner_signed_at)}
                    </p>
                  ) : null}
                  {barterAgreement?.studio_signed_at ? (
                    <p className="text-[var(--t-accent)]">
                      TAĪSTU signed {formatReviewStamp(barterAgreement.studio_signed_at)}
                    </p>
                  ) : null}
                </div>
              ) : barterAgreement?.shared_at ? (
                <p className="text-xs text-[var(--t-meta)]">
                  Shared {formatReviewStamp(barterAgreement.shared_at)}
                </p>
              ) : null}
              {canCountersignBarter ? (
                <BarterAgreementSignBox
                  label="TAĪSTU · Taī"
                  partyLabel="Studio"
                  submitLabel="Sign & send copy"
                  busy={barterSignBusy}
                  onSign={countersignBarterAgreement}
                />
              ) : null}
            </div>
          ) : uploadedProposal ? (
            <div className="space-y-4" data-testid="prospect-uploaded-proposal-mock">
              <div>
                {uploadedProposal.status === "accepted" ||
                uploadedProposal.status === "changes_requested" ? (
                  <div className="t-meta">
                    {uploadedProposal.status === "accepted" ? "Accepted" : "Changes requested"}
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={openProposalFile}
                  className="mt-1 block text-left font-heading text-base !font-light tracking-tight hover:text-[var(--t-accent)]"
                  data-testid="prospect-proposal-open-file"
                >
                  {uploadedProposal.fileName}
                </button>
                {formatDocumentDate(uploadedProposal.sharedAt || uploadedProposal.uploadedAt) ? (
                  <p className="mt-0.5 text-xs text-[var(--t-meta)]">
                    {formatDocumentDate(uploadedProposal.sharedAt || uploadedProposal.uploadedAt)}
                  </p>
                ) : null}
              </div>

              <button
                type="button"
                onClick={() => setProposalActivityOpen((v) => !v)}
                className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]"
                data-testid="prospect-proposal-activity-toggle"
                aria-expanded={proposalActivityOpen}
              >
                {proposalActivityOpen ? "Hide activity" : "Activity"}
              </button>

              {proposalActivityOpen ? (
                <div
                  className="space-y-1 border-t border-black/5 pt-3 text-xs text-[var(--t-meta)]"
                  data-testid="prospect-proposal-activity"
                >
                  {uploadedProposal.uploadedAt ? (
                    <p>Uploaded {formatReviewStamp(uploadedProposal.uploadedAt)}</p>
                  ) : null}
                  {uploadedProposal.sharedAt ? (
                    <p>Shared {formatReviewStamp(uploadedProposal.sharedAt)}</p>
                  ) : null}
                  {uploadedProposal.openedAt ? (
                    <p className="text-[var(--t-accent)]" data-testid="prospect-proposal-opened">
                      Opened {formatReviewStamp(uploadedProposal.openedAt)}
                      {uploadedProposal.openCount > 1
                        ? ` · ${uploadedProposal.openCount} views`
                        : ""}
                    </p>
                  ) : uploadedProposal.sharedAt ? (
                    <p data-testid="prospect-proposal-unopened">Not opened yet</p>
                  ) : null}
                  {uploadedProposal.status === "accepted" ? (
                    <p className="text-[var(--t-text)]" data-testid="prospect-uploaded-accepted">
                      Accepted {uploadedProposal.acceptedOptionLabel}
                      {uploadedProposal.acceptedAt
                        ? ` · ${formatReviewStamp(uploadedProposal.acceptedAt)}`
                        : ""}
                    </p>
                  ) : null}
                  {uploadedProposal.status === "accepted" && uploadedProposal.acceptedAddon ? (
                    <p data-testid="prospect-proposal-accepted-addon">
                      Add-on · {uploadedProposal.acceptedAddon.title}
                      {uploadedProposal.acceptedAddon.amount
                        ? ` · $${Number(uploadedProposal.acceptedAddon.amount).toLocaleString()}`
                        : ""}
                    </p>
                  ) : null}
                  {uploadedProposal.changesRequestedAt ? (
                    <p className="text-[var(--t-warn)]">
                      Changes requested {formatReviewStamp(uploadedProposal.changesRequestedAt)}
                    </p>
                  ) : null}
                  {uploadedProposal.responseNote ? (
                    <p>Note: {uploadedProposal.responseNote}</p>
                  ) : null}
                  <div className="flex flex-wrap gap-3 pt-2">
                    <button
                      type="button"
                      onClick={refreshUploaded}
                      className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-accent)]"
                      data-testid="prospect-proposal-refresh"
                    >
                      Refresh
                    </button>
                    {isLocalApp() ? (
                      <button
                        type="button"
                        onClick={() => {
                          setUploadedProposal(resetUploadedProposal(clientId, client?.name));
                          toast.message("Mock proposal reset");
                        }}
                        className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-accent)]"
                        data-testid="prospect-proposal-reset-mock"
                      >
                        Reset mock
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          clearUploadedProposal(clientId);
                          setUploadedProposal(null);
                          toast.message("Proposal cleared");
                        }}
                        className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-accent)]"
                        data-testid="prospect-proposal-clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <div
                className={
                  scopeReview?.status === "pending"
                    ? "text-[var(--t-pink)]"
                    : "t-meta"
                }
                data-testid="prospect-scope-status-label"
              >
                {scopeReview?.status
                  ? scopeReviewStatusLabel(scopeReview.status, { barter: isBarter })
                  : "Not shared yet"}
              </div>

              {scopeReview?.status === "accepted" ? (
                <div
                  className="neo-well mt-4 px-4 py-3"
                  data-testid="prospect-scope-accepted-card"
                >
                  <p className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]">
                    {isBarter ? "Agreement signed" : "Scope accepted"}
                  </p>
                  <p className="mt-1 text-sm text-[var(--t-text)]" data-testid="prospect-scope-accepted-at">
                    {isBarter && scopeReview.signature_name
                      ? `${scopeReview.signature_name} · `
                      : scopeReview.accepted_option_label
                        ? `${scopeReview.accepted_option_label} · `
                        : ""}
                    {formatReviewStamp(scopeReview.signed_at || scopeReview.accepted_at) || "—"}
                  </p>
                  {!isBarter && scopeReview.response_note ? (
                    <p className="mt-2 text-xs text-[var(--t-meta)]">Note: {scopeReview.response_note}</p>
                  ) : null}
                </div>
              ) : (
                <div className="mt-3 space-y-1 text-xs text-[var(--t-meta)]">
                  {scopeReview?.shared_at ? (
                    <p>Shared {formatReviewStamp(scopeReview.shared_at)}</p>
                  ) : null}
                  {!isBarter && scopeReview?.changes_requested_at ? (
                    <p className="text-[var(--t-warn)]" data-testid="prospect-scope-changes-at">
                      Changes requested {formatReviewStamp(scopeReview.changes_requested_at)}
                    </p>
                  ) : null}
                  {!isBarter && scopeReview?.response_note ? (
                    <p>Note: {scopeReview.response_note}</p>
                  ) : null}
                </div>
              )}
            </>
          )}
        </section>

        {!isBarter ? (
        <section className="neo-panel p-5 md:p-6" data-testid="prospect-invoices">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="label-eyebrow mb-0">Invoice</div>
            <button
              type="button"
              className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]"
              onClick={() => setShowInvoice(true)}
              data-testid="prospect-create-invoice"
            >
              New invoice
            </button>
          </div>
          {invoices.length === 0 ? (
            <div className="t-meta">No invoices yet.</div>
          ) : (
            <div className="divide-y divide-black/5">
              {invoices.map((inv) => (
                <Link
                  key={inv.id || inv.invoiceNumber}
                  to="/admin/revenue"
                  className="flex items-center justify-between gap-3 py-3 text-sm transition-colors first:pt-0 last:pb-0 hover:text-[var(--t-accent)]"
                  data-testid={`prospect-invoice-${inv.id}`}
                >
                  <span className="font-heading !font-light">
                    {inv.invoiceNumber || inv.invoice_number || "Invoice"}
                  </span>
                  <span className="text-xs uppercase tracking-[0.14em] text-[var(--t-meta)]">
                    {inv.status || "—"}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>
        ) : null}

        <section className="neo-panel p-5 md:p-6" data-testid="prospect-notes-section">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="label-eyebrow mb-0">Prospect notes</div>
            {prospectNotesSaveState !== "idle" ? (
              <span
                className={`text-[11px] tracking-[0.04em] ${
                  prospectNotesSaveState === "error"
                    ? "text-[var(--t-error)]"
                    : "text-[var(--t-meta)]"
                }`}
                data-testid="prospect-record-save-status"
              >
                {prospectNotesSaveState === "saving"
                  ? "Saving…"
                  : prospectNotesSaveState === "saved"
                    ? "Saved"
                    : "Couldn’t save"}
              </span>
            ) : null}
          </div>
          <textarea
            className="t-textarea min-h-[160px] w-full !rounded-xl !bg-white/50"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="General context, not tied to a specific meeting…"
            data-testid="prospect-notes"
          />
        </section>
      </div>

      {showInvoice ? (
        <InvoiceCreateDialog
          open={showInvoice}
          onOpenChange={setShowInvoice}
          defaultClientId={client.id}
          onCreated={() => {
            setShowInvoice(false);
            load();
          }}
        />
      ) : null}

      {fieldsOpen ? (
        <AdminSideDrawer
          open={fieldsOpen}
          onClose={() => {
            if (!fieldsBusy) setFieldsOpen(false);
          }}
          eyebrow={docLabel}
          title={isBarter ? "Edit agreement" : fieldsMode === "one" ? "Edit proposal" : "Edit options"}
          testId="prospect-proposal-fields-drawer"
          wide
          footer={
            <>
              <button
                type="button"
                className="t-btn-secondary"
                onClick={() => setFieldsOpen(false)}
                disabled={fieldsBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="t-btn-primary"
                onClick={saveFieldsEditor}
                disabled={fieldsBusy}
                data-testid="prospect-proposal-fields-save"
              >
                {fieldsBusy ? "Saving…" : "Save"}
              </button>
            </>
          }
        >
          <DrawerFormBody>
            <div className="block" data-testid="prospect-fields-exchange">
              <span className="label-eyebrow mb-1.5 block">Exchange</span>
              <div className="relative">
                <select
                  className="t-select h-[45px] w-full appearance-none !rounded-xl !bg-white !px-4 !pr-10"
                  value={isBarter ? "barter" : "cash"}
                  onChange={(e) => setCompensationType(e.target.value)}
                  data-testid="prospect-fields-compensation"
                >
                  <option value="cash">Invoice</option>
                  <option value="barter">In-kind</option>
                </select>
              </div>
            </div>
            {isBarter ? (
              <>
                <p className="text-xs text-[var(--t-meta)]" data-testid="prospect-barter-editor-note">
                  Legal clauses stay fixed. Edit only the exchange fields that change per partner.
                </p>
                {BARTER_EDITABLE_FIELDS.map((field) => (
                  <div key={field.key} className="block" data-testid={`prospect-barter-field-${field.key}`}>
                    <span className="label-eyebrow mb-1.5 block">{field.label}</span>
                    {field.type === "date" ? (
                      <InlineDatePickerField
                        value={toBarterStartDateIso(barterFieldsDraft.START_DATE) || ""}
                        onChange={(next) =>
                          setBarterFieldsDraft((prev) => ({
                            ...prev,
                            START_DATE: next || "",
                          }))
                        }
                        placeholder={field.placeholder}
                        allowClear
                        data-testid="prospect-barter-start-date"
                      />
                    ) : field.multiline ? (
                      <textarea
                        className="t-textarea min-h-[72px] w-full"
                        value={barterFieldsDraft[field.key] || ""}
                        onChange={(e) =>
                          setBarterFieldsDraft((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))
                        }
                        placeholder={field.placeholder}
                      />
                    ) : (
                      <input
                        className="t-input w-full"
                        value={barterFieldsDraft[field.key] || ""}
                        onChange={(e) =>
                          setBarterFieldsDraft((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))
                        }
                        placeholder={field.placeholder}
                      />
                    )}
                  </div>
                ))}
              </>
            ) : (
              <>
            <div>
              <div className="label-eyebrow mb-2">Structure</div>
              <div className="flex gap-2" data-testid="prospect-proposal-structure">
                <button
                  type="button"
                  onClick={() => setProposalFieldsMode("one")}
                  className={cn(
                    "rounded-[var(--t-event-pill-radius)] border px-3 py-2 text-[10px] uppercase tracking-[0.14em] transition-colors",
                    fieldsMode === "one"
                      ? "border-[var(--t-accent)] bg-[var(--t-accent)]/5 text-[var(--t-accent)]"
                      : "border-[var(--t-border)] text-[var(--t-meta)] hover:text-[var(--t-text)]",
                  )}
                  data-testid="prospect-proposal-mode-one"
                >
                  One proposal
                </button>
                <button
                  type="button"
                  onClick={() => setProposalFieldsMode("multiple")}
                  className={cn(
                    "rounded-[var(--t-event-pill-radius)] border px-3 py-2 text-[10px] uppercase tracking-[0.14em] transition-colors",
                    fieldsMode === "multiple"
                      ? "border-[var(--t-accent)] bg-[var(--t-accent)]/5 text-[var(--t-accent)]"
                      : "border-[var(--t-border)] text-[var(--t-meta)] hover:text-[var(--t-text)]",
                  )}
                  data-testid="prospect-proposal-mode-multiple"
                >
                  Multiple options
                </button>
              </div>
            </div>

            {fieldsDraft.map((opt, idx) => (
              <div
                key={idx}
                className="space-y-3 border-t border-black/5 pt-4 first:border-t-0 first:pt-0"
                data-testid={`prospect-proposal-field-option-${idx}`}
              >
                {fieldsMode === "multiple" ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="label-eyebrow mb-0">Option {idx + 1}</div>
                    {fieldsDraft.length > 1 ? (
                      <button
                        type="button"
                        className="text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-accent)]"
                        onClick={() => setFieldsDraft((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <label className="block">
                  <span className="label-eyebrow mb-1.5 block">
                    {fieldsMode === "one" ? "Title" : "Option name"}
                  </span>
                  <input
                    className="t-input w-full"
                    value={opt.option_label}
                    onChange={(e) => updateFieldOption(idx, { option_label: e.target.value })}
                    placeholder={fieldsMode === "one" ? "Proposal" : "Notion OS"}
                    data-testid={`prospect-proposal-label-${idx}`}
                  />
                </label>
                <label className="block">
                  <span className="label-eyebrow mb-1.5 block">Timeline</span>
                  <select
                    className="t-input w-full"
                    value={
                      PROPOSAL_TIMELINES.includes(opt.timeline)
                        ? opt.timeline
                        : PROPOSAL_TIMELINES[0]
                    }
                    onChange={(e) => updateFieldOption(idx, { timeline: e.target.value })}
                    data-testid={`prospect-proposal-timeline-${idx}`}
                  >
                    {PROPOSAL_TIMELINES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="label-eyebrow mb-1.5 block">Intro</span>
                  <textarea
                    className="t-textarea min-h-[72px] w-full"
                    value={opt.intro}
                    onChange={(e) => updateFieldOption(idx, { intro: e.target.value })}
                    placeholder="This proposal outlines…"
                  />
                </label>
                <label className="block">
                  <span className="label-eyebrow mb-1.5 block">
                    {opt.timeline === "3 months" || opt.timeline === "Ongoing"
                      ? "Investment (USD / month)"
                      : "Investment (USD)"}
                  </span>
                  <input
                    className="t-input w-full"
                    type="number"
                    min="0"
                    step="100"
                    value={opt.investment_total}
                    onChange={(e) => updateFieldOption(idx, { investment_total: e.target.value })}
                    placeholder="2500"
                  />
                </label>
                {(opt.scope_items || []).map((item, itemIdx) => (
                  <div key={itemIdx} className="space-y-2 rounded-xl bg-black/[0.02] p-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)]">
                      Scope {String(itemIdx + 1).padStart(2, "0")}
                    </div>
                    <input
                      className="t-input w-full"
                      value={item.title}
                      onChange={(e) =>
                        updateFieldScopeItem(idx, itemIdx, { title: e.target.value })
                      }
                      placeholder="Title"
                    />
                    <textarea
                      className="t-textarea min-h-[56px] w-full"
                      value={item.description}
                      onChange={(e) =>
                        updateFieldScopeItem(idx, itemIdx, { description: e.target.value })
                      }
                      placeholder="Description"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  className="text-[10px] uppercase tracking-[0.14em] text-[var(--t-accent)]"
                  onClick={() =>
                    updateFieldOption(idx, {
                      scope_items: [...(opt.scope_items || []), { title: "", description: "" }],
                    })
                  }
                >
                  Add scope item
                </button>
                <div
                  className="space-y-2 rounded-xl border border-dashed border-black/10 p-3"
                  data-testid={`prospect-proposal-addon-${idx}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="label-eyebrow mb-0">Add-on</div>
                    <Switch
                      checked={Boolean(opt.addon_enabled)}
                      onCheckedChange={(on) =>
                        updateFieldOption(
                          idx,
                          on
                            ? { addon_enabled: true }
                            : {
                                addon_enabled: false,
                                addon_title: "",
                                addon_description: "",
                                addon_amount: "",
                              },
                        )
                      }
                      className="data-[state=checked]:bg-[var(--t-accent)]"
                      data-testid={`prospect-proposal-addon-toggle-${idx}`}
                      aria-label="Include add-on"
                    />
                  </div>
                  {opt.addon_enabled ? (
                    <>
                      <label className="block">
                        <span className="label-eyebrow mb-1.5 block">Title</span>
                        <input
                          className="t-input w-full"
                          value={opt.addon_title || ""}
                          onChange={(e) => updateFieldOption(idx, { addon_title: e.target.value })}
                          placeholder="Assistant onboarding session"
                          data-testid={`prospect-proposal-addon-title-${idx}`}
                        />
                      </label>
                      <label className="block">
                        <span className="label-eyebrow mb-1.5 block">Description</span>
                        <textarea
                          className="t-textarea min-h-[56px] w-full"
                          value={opt.addon_description || ""}
                          onChange={(e) =>
                            updateFieldOption(idx, { addon_description: e.target.value })
                          }
                          placeholder="What they’re getting if they check the box."
                          data-testid={`prospect-proposal-addon-description-${idx}`}
                        />
                      </label>
                      <label className="block">
                        <span className="label-eyebrow mb-1.5 block">Amount (USD)</span>
                        <input
                          className="t-input w-full"
                          type="number"
                          min="0"
                          step="50"
                          value={opt.addon_amount ?? ""}
                          onChange={(e) => updateFieldOption(idx, { addon_amount: e.target.value })}
                          placeholder="500"
                          data-testid={`prospect-proposal-addon-amount-${idx}`}
                        />
                      </label>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
            {fieldsMode === "multiple" ? (
              <button
                type="button"
                className="neo-cta w-full justify-center"
                onClick={() =>
                  setFieldsDraft((prev) => [
                    ...prev,
                    {
                      option_label: "",
                      intro: "",
                      timeline: PROPOSAL_TIMELINES[0],
                      investment_total: "",
                      exchange: "",
                      terms: "",
                      scope_items: [{ title: "", description: "" }],
                      addon_enabled: false,
                      addon_title: "",
                      addon_description: "",
                      addon_amount: "",
                    },
                  ])
                }
                data-testid="prospect-proposal-add-option"
              >
                Add option
              </button>
            ) : null}
              </>
            )}
          </DrawerFormBody>
        </AdminSideDrawer>
      ) : null}
    </div>
  );
}
